"use client";
import React, { useState } from "react";
import { formatDate } from "@/utils/datetimeUtils";
import {
  Function,
  PersonData,
  PersonDataTournamentStatistics,
  PlayerTournamentStatistics,
  SquadPositionPlayer,
} from "@/sources/kicker-amateur/types";
import { getAge } from "@/helpers/dateHelper";
import ChevronDown from "@/assets/icon/chevron-down.svg";
import YellowCard from "@/assets/icon/ticker/yellow-card.svg";
import YellowRedCard from "@/assets/icon/ticker/yellow-red-card.svg";
import RedCard from "@/assets/icon/ticker/red-card.svg";
import { Card } from "@/app/pwa/components/Card";;
import Container from "@/app/pwa/components/Container";
import Flag from "react-world-flags";
import { Can } from "@/app/pwa/lib/ability-context";
import { subject } from "@casl/ability";
import Button, { ButtonColors } from "@/app/pwa/components/Button";
import IconSend from "@/assets/icon/send.svg";
import { usePlayerModal } from "@/hooks/usePlayerModal";
import { InvitationOptionsModal } from "@/app/pwa/components/modals/InvitationOptionsModal";
import { UserInfo } from "@/sources/kicker-ovsyndication";
import FunctionImage from "@/app/pwa/components/FunctionImage";
import { getFlagCode } from "@/helpers/nationshelper";
import { getPersonCountryData } from "@/helpers/personHelpers";

interface TeamPlayerStatisticTournamentProps {
  tournament: any;
  statistic: PersonDataTournamentStatistics;
}

const Statistic: React.FC<{ title: string; value: React.ReactNode }> = ({
  title,
  value,
}) => (
  <>
    <div className="font-semibold text-left">{title}</div>
    <div>{value}</div>
  </>
);

function TeamPlayerStatisticTournament({
  tournament,
  statistic,
}: TeamPlayerStatisticTournamentProps) {
  return (
    <div className="pl-4">
      <h4 className="text-center text-lg mt-4 font-semibold">
        {statistic.Name} – {tournament.Name}
      </h4>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 max-w-[12rem] mx-auto">
        <Statistic title="Spiele" value={tournament.Appearances} />
        <Statistic
          title="Spielminuten"
          value={new Intl.NumberFormat("de").format(tournament.PlayedMinutes)}
        />
        <Statistic title="Tore" value={tournament.Goals} />
        <Statistic
          title="Karten"
          value={
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-0.5">
                <YellowCard className="w-4 h-4 inline-block" />
                {tournament.YellowCards}
              </div>
              <div className="flex items-center gap-0.5">
                <YellowRedCard className="w-4 h-4 inline-block" />
                {tournament.YellowRedCards}
              </div>
              <div className="flex items-center gap-0.5">
                <RedCard className="w-4 h-4 inline-block" />
                {tournament.RedCards}
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
}

interface TeamPlayerStatisticsProps {
  person: any;
}
export function TeamPlayerStatistics({ person }: TeamPlayerStatisticsProps) {
  const [showAll, setShowAll] = useState<boolean>(false);

  const seasons = person?.Statistics?.seasons as {
    Name: string;
    Tournaments: any[];
  }[];

  let seasonsToShow: PersonDataTournamentStatistics[] = [];

  if (seasons && seasons.length > 0) {
    if (showAll) {
      seasonsToShow = seasons;
    } else {
      seasonsToShow = [seasons[0]];
    }
  }

  if (seasonsToShow.length === 0) {
    return <></>;
  }

  return (
    <Container className="mt-section">
      <Card
        p={false}
        className="card"
        header={<h3 className="font-semibold text-center">Statistik</h3>}
      >
        <div className="pb-6">
          {seasonsToShow.map(
            (statistic: PersonDataTournamentStatistics, index: number) => (
              <div
                key={`TeamPlayerStatistics-statistic-${statistic.Name}-${index}`}
                className="mt-6 border-t border-gray-150 first:border-t-0"
              >
                {/* <div className="text-center border-b border-gray-150 text-lg font-semibold">{statistic.Name}</div> */}
                <div className="">
                  {statistic.Tournaments.map(
                    (tournament: PlayerTournamentStatistics) => (
                      <TeamPlayerStatisticTournament
                        key={`TeamPlayerStatistics-tournament-${statistic.Name}-${index}-${tournament.Name}`}
                        tournament={tournament}
                        statistic={statistic}
                      />
                    )
                  )}
                </div>
              </div>
            )
          )}
        </div>
        {!showAll && seasons && seasons.length > 1 && (
          <div className="border-t border-gray-150">
            <button
              type="button"
              className="button button--link-secondary w-full justify-center"
              onClick={() => setShowAll(true)}
            >
              <ChevronDown className="button__icon--left" />
              Alle Statistiken anzeigen
            </button>
          </div>
        )}
      </Card>
    </Container>
  );
}

export function TeamPlayer({
  person,
  teamId,
  playerId,
  noImageCache
}: {
  person: PersonData;
  teamId: number;
  playerId: number;
  noImageCache?:boolean
}) {
  const countryCode = getPersonCountryData(person);
  const personFunction = person.Functions?.find(func => func.OrganizationId=== teamId)

  const isInFuture = new Date((person.Function as Function)?.Start!) > new Date();

  let useAspectsImageFromSrc = personFunction?.MediaServiceImages?.find(image => image.Gid === personFunction?.Id)?.AbsoluteUrl;
  if(!useAspectsImageFromSrc){
    useAspectsImageFromSrc = personFunction?.MediaServiceImages?.find(image => image.Gid === person?.Id)?.AbsoluteUrl;
  }

  return (
    <div className="@container mt-4">
      <div className="flex gap-4 lg:gap-5 pb-4">
        <div className="w-16 @md:ml-0 flex-shrink-0">
          <FunctionImage imageClassName="rounded-l-none @md:rounded-l-lg w-full" player={person} useAspectsImageFromSrc={useAspectsImageFromSrc} functionId={personFunction?.Id} size="lg" noCache={noImageCache} />
        </div>
        <div className="space-y-2 min-w-0 flex-1">
          <div>
            <div className="text-xl font-semibold break-anywhere">{`${person.FirstName} ${person.LastName}`}</div>
            {personFunction?.FunctionType && (
              <div>{personFunction?.FunctionType.Name}</div>
            )}
          </div>
          {/* <div className="flex flex-wrap gap-2 gap-x-10"> */}
          <div className="flex flex-wrap gap-2 gap-x-8 justify-between pr-6 max-w-lg">
            {person.Birthdate && (
              <div>
                <div className="font-semibold">Alter</div>
                <div>{`${getAge(
                  new Date(person.Birthdate)
                )} Jahre (${formatDate(person.Birthdate)})`}</div>
              </div>
            )}
            {person?.Function && (person.Function as Function).Start && (
              <div className="">
                { isInFuture
                  ?
                    <div className="font-semibold">Im Team ab</div>
                  :
                    <div className="font-semibold">Im Team seit</div>
                }
                <div>{formatDate((person.Function as Function)?.Start!)}</div>
              </div>
            )}
            {person?.Function && (person.Function as Function).End && (
              <div className="">
                <div className="font-semibold">Im Team bis</div>
                <div>{formatDate((person.Function as Function)?.End!)}</div>
              </div>
            )}
            {countryCode && (
              <div className="">
                <div className="font-semibold">Nationalität</div>
                <div className="flex items-center gap-1.5">
                  {countryCode.code && (<Flag
                      code={getFlagCode(countryCode.code)}
                      width={24}
                      height={24}
                      className="w-6 h-6 object-contain"
                    />
                  )}{" "}
                  {countryCode.name}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface PersonInviteButtonProps {
  person: PersonData;
  user: UserInfo;
  clubId: number;
  color?: ButtonColors;
}
export function PersonInviteButton({ person, user, clubId, color }: PersonInviteButtonProps) {
  const {
    isOpen: isInviteOpen,
    selectedPlayer: selectedPlayerForInvite,
    openModal: openInviteModal,
    closeModal: closeInviteModal,
  } = usePlayerModal();

  return (
    <>
      <Can
        do="create"
        on={subject("Invitation", {
          personId: `${person?.Id.toString()}`,
          contextId: `${person?.Team?.Id?.toString()}`,
          contextType: "Team" as "Team",
        })}
      >
        <div className="flex justify-center">
          <Button
            color={color || "secondary"}
            className="inline-flex w-full justify-center max-w-md text-center"
            onClick={() => openInviteModal(person)}
          >
            <span className="text-ellipsis overflow-hidden mr-blank">
              {person?.FirstName}
            </span>
            einladen
            <IconSend className="button__icon" />
          </Button>
        </div>
      </Can>
      {isInviteOpen && (
        <InvitationOptionsModal
          isOpen={isInviteOpen}
          team={person.Team!}
          onRequestClose={closeInviteModal}
          player={selectedPlayerForInvite as SquadPositionPlayer}
          user={user}
          clubId={clubId}
        />
      )}
    </>
  );
}
